import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    EventEmitter,
    inject,
    OnInit,
    Output,
    signal,
    computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// BMW DS Components
import {
    DsBoxModule,
    DsButtonModule,
    DsCheckboxModule,
    DsColumnData,
    DsFormFieldModule,
    DsIconModule,
    DsLoadingAreaConfiguration,
    DsLoadingAreaModule,
    DsLoadingAreaService,
    DsModalModule,
    DsPopoverModule,
    DsProgressCircleModule,
    DsSelectModule,
    DsTableModule,
    DsToastModel,
    DsToastService,
    SelectListOption,
} from '@bmw-ds/components';

// Local Models & Services
import { CriteriaStoreService } from '../../services/criteria-store.service';
import { CriteriaService } from '../../services/criteria.service';
import { ReplenishmentService } from '../../services/replenishment.service';
import { CriteriaResponse, DealerModel, Version } from '../../models/criteria.model';
import { ReplenishmentStockRowData } from '../../models/replenishment-stock-row.model';
import { StockReplenishmentItem } from '../../models/replenishment-stock-response.model';
import { ReplenishmentRequest } from '../../models/replenishment-request.model';
import { RequestReplenishmentRequest } from '../../models/request-vehicle.model';
import { COMMON_IMPORTS } from '../../shared/common-imports';

// Types for better safety
interface FilterState {
    brand: string;
    brandName: string;
    version: string;
    model: string;
    storageLocation: string;
}

interface ModalState {
    vin: string;
    description: string;
    storageLocation: string;
    selectedReplVehicleModelCode: string;
    selectedReplVehicleVersion: string;
    selectedReplVehiclePlantCode: string;
}

@Component({
    selector: 'app-replenishment-stock-filters',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DsCheckboxModule,
        DsFormFieldModule,
        DsSelectModule,
        DsLoadingAreaModule,
        DsButtonModule,
        DsTableModule,
        DsPopoverModule,
        DsBoxModule,
        DsIconModule,
        DsModalModule,
        DsProgressCircleModule,
        COMMON_IMPORTS
    ],
    templateUrl: './replenishment-stock-filters.component.html',
    styleUrl: './replenishment-stock-filters.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReplenishmentStockFiltersComponent implements OnInit {
    // Services
    private readonly criteriaStoreService = inject(CriteriaStoreService);
    private readonly criteriaService = inject(CriteriaService);
    private readonly replenishmentService = inject(ReplenishmentService);
    private readonly loadingAreaService = inject(DsLoadingAreaService);
    private readonly toastService = inject(DsToastService);
    private readonly destroyRef = inject(DestroyRef);

    // Constants
    private readonly LOADING_ID = 'bodyLoading';
    readonly columnData: DsColumnData[] = [
        { field: 'VIN', headerName: 'VIN' },
        { field: 'modelCode', headerName: 'Model Code' },
        { field: 'description', headerName: 'Description' },
        { field: 'interior', headerName: 'Interior' },
        { field: 'exterior', headerName: 'Exterior' },
        { field: 'optionCount', headerName: 'Option Count' },
        { field: 'download', headerName: '' },
    ];
    readonly storageLocationOptions: SelectListOption[] = [
        { id: 'NSCO', label: 'NSCOU' },
        { id: 'BASE', label: 'BASEMENT' },
    ];

    onStorageLocationChange(event: SelectListOption | SelectListOption[] | null): void {
        if (this.isSingleSelection(event)) {
            this.filters.update(f => ({ ...f, storageLocation: event.id }));
        } else {
            this.filters.update(f => ({ ...f, storageLocation: '' }));
        }
    }
    // Outputs
    @Output() filtersChanged = new EventEmitter<ReplenishmentRequest>();

    // State Signals
    readonly rowsData = signal<ReplenishmentStockRowData[]>([]);
    readonly hasSearched = signal(false);
    readonly StockReplshModalIsOpen = signal(false);

    // Filter State
    readonly filters = signal<FilterState>({ brand: '', brandName: '', version: '', model: '', storageLocation: '' });

    // Data Source Signals
    readonly brandOptions = signal<{ id: string; label: string }[]>([]);
    private readonly allVersions = signal<Version[]>([]);
    private readonly allModels = signal<DealerModel[]>([]);
    private readonly criteriaData = signal<CriteriaResponse | null>(null);

    // Modal State Signal
    readonly modalData = signal<ModalState>({
        vin: '',
        description: '',
        storageLocation: '',
        selectedReplVehicleModelCode: '',
        selectedReplVehicleVersion: '',
        selectedReplVehiclePlantCode: '',
    });

    // Computed Values (Derived State)
    readonly isBrandSelected = computed(() => !!this.filters().brand);
    readonly isStorageLocationSelected = computed(() => !!this.filters().storageLocation);

    readonly filteredVersionOptions = computed(() => {
        const brand = this.filters().brand;
        if (!brand) return [];
        return this.allVersions()
            .filter((v) => String(v.brand) === String(brand))
            .map((v) => ({ id: v.version, label: v.version }));
    });

    readonly filteredModelOptions = computed(() => {
        const { brand, version } = this.filters();

        // First filter by Brand
        let models = this.allModels().filter((m) => String(m.brand) === String(brand));

        // Then filter by Version if selected
        if (version) {
            models = models.filter((m) => m.version === version);
        }

        return models.map((m) => ({
            id: m.modelCode,
            label: `${m.modelCode} - ${m.profileModelDescription}`,
        }));
    });

    // Helper computed for the Request Payload model filtering
    private readonly filteredModelOptionsReq = computed(() => {
        const { model } = this.filters();
        const currentOptions = this.filteredModelOptions();

        if (model) {
            // If a specific model is selected, return only that one
            return currentOptions.filter(opt => opt.id === model);
        }
        return currentOptions;
    });

    ngOnInit(): void {
        this.setLoading(true);
        this.populateDropdowns();
    }

    // --- Data Loading Logic ---

    private populateDropdowns(): void {
        const store = this.criteriaStoreService.getCriteriaData();

        if (store) {
            store.pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((data) => this.handleCriteriaData(data));
        } else {
            const dealerCode = localStorage.getItem('dealerCode') ?? '';
            this.criteriaService.postCriteriaSelection(dealerCode)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((data) => {
                    this.criteriaStoreService.setCriteriaData(data);
                    this.handleCriteriaData(data);
                });
        }
    }

    private handleCriteriaData(data: CriteriaResponse | null): void {
        this.setLoading(false);
        if (!data) return;

        this.criteriaData.set(data);

        this.brandOptions.set(data.dealerBrands.map((b) => ({
            id: b.brandCode,
            label: b.brandDescription,
        })));

        this.allVersions.set(data.versions);
        this.allModels.set(data.dealerModels);
    }

    // --- Filter Handlers ---

    onBrandChange(event: SelectListOption | SelectListOption[] | null): void {
        if (this.isSingleSelection(event)) {
            this.setLoading(true);

            this.filters.update(f => ({
                ...f,
                brand: event.id,
                brandName: event.label,
                version: '', // Reset cascading filters
                model: ''
            }));

            // Simulate network delay or just clear loader
            setTimeout(() => this.setLoading(false), 200);
        }
    }

    onVersionChange(event: SelectListOption | SelectListOption[] | null): void {
        if (this.isSingleSelection(event)) {
            this.filters.update(f => ({ ...f, version: event.label, model: '' }));
            this.emitFiltersChange();
        } else {
            this.resetVersionAndModel();
        }
    }

    onModelChange(event: SelectListOption | SelectListOption[] | null): void {
        if (this.isSingleSelection(event)) {
            this.filters.update(f => ({ ...f, model: event.id }));
        } else {
            this.filters.update(f => ({ ...f, model: '' }));
            this.emitFiltersChange();
        }
    }

    // --- Search Logic ---

    loadReplenishmentData(): void {
        this.setLoading(true, 'Stock Replenishment');
        this.hasSearched.set(true);

        const payload = this.createRequestPayload();

        this.replenishmentService.getReplenishmentVehicle(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    const messageType = response?.message?.messageType?.trim();
                    const messageText = response?.message?.messageText?.trim();

                    if (messageType && messageText && !(response.items?.length)) {
                        // Show backend message as an informational toast only when there are no items to display.
                        this.addToast('neutral', `${messageText}`);
                    }

                    if (response?.message?.messageType === 'E') {
                        this.rowsData.set([]);
                    } else {
                        const mappedItems = (response.items || []).map(this.mapStockItem);
                        this.rowsData.set(mappedItems);
                    }
                    this.setLoading(false);
                },
                error: (err) => {
                    console.error('Error loading stock', err);
                    this.rowsData.set([]);
                    this.setLoading(false);
                    this.addToast('critical', 'Failed to load stock data.');
                },
            });
    }

    // --- Modal & Action Logic ---

    openRtpUploadDownloadModal(row: ReplenishmentStockRowData): void {
        this.modalData.set({
            vin: row.VIN,
            description: row.description,
            storageLocation: row.storageLocation || this.filters().storageLocation,
            selectedReplVehicleModelCode: row.modelCode,
            selectedReplVehicleVersion: row.version,
            selectedReplVehiclePlantCode: row.plantCode,
        });
        this.StockReplshModalIsOpen.set(true);
    }

    requestVehicle(): void {
        this.setLoading(true, 'Requesting Vehicle');
        const modal = this.modalData();
        const criteria = this.criteriaData();

        if (!criteria) {
            this.setLoading(false);
            return;
        }

        const request: RequestReplenishmentRequest = {
            vin: modal.vin,
            dealerCode: localStorage.getItem('dealerCode') ?? '',
            plantCode: criteria.plant,
            storageLocation: modal.storageLocation || this.filters().storageLocation,
            model: modal.selectedReplVehicleModelCode,
            version: modal.selectedReplVehicleVersion,
            werks: modal.selectedReplVehiclePlantCode,
            purpose: '',
        };

        this.replenishmentService.requestReplenishmentVehicle(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.setLoading(false);
                    const type = response.messageType === 'E' ? 'critical' : 'positive';
                    this.addToast(type, response.messageText);
                    this.StockReplshModalIsOpen.set(false);

                    if (response.messageType !== 'E') {
                        this.loadReplenishmentData();
                    }
                },
                error: (error) => {
                    this.setLoading(false);
                    console.error('Error requesting vehicle', error);
                    this.addToast('critical', this.getErrorMessage(error));
                    this.StockReplshModalIsOpen.set(false);
                },
            });
    }

    private createRequestPayload(): ReplenishmentRequest {
        const { brand, brandName, version, model, storageLocation } = this.filters();
        const criteria = this.criteriaData();

        return {
            plant: criteria?.plant ?? '',
            model: model,
            brandName: brandName,
            brand: brand,
            storageLocation: storageLocation,
            version: {
                brand: brand,
                vehicleType: this.getVehicleType(),
                version: version,
            },
            materialNr: this.filteredModelOptionsReq().map((o) => o.id),
        };
    }

    private mapStockItem(item: StockReplenishmentItem): ReplenishmentStockRowData {
        let interior = '';
        let exterior = '';

        // Logic to extract Interior/Exterior from options
        if (Array.isArray(item.options)) {
            for (const opt of item.options) {
                if (opt.optionIndicator === 'L' && !exterior) exterior = opt.optionDescription;
                if (opt.optionIndicator === 'P' && !interior) interior = opt.optionDescription;
                if (interior && exterior) break;
            }
        }

        return {
            image: '',
            VIN: item.vin,
            modelCode: item.modelCode,
            description: item.profileModelDescription,
            interior: interior ?? item.interior ?? '',
            exterior: exterior ?? item.exterior ?? '',
            optionCount: item.optionsCount,
            storageLocation: item.storageLocation,
            options: item.options ?? [],
            version: item.version,
            plantCode: item.plantCode,
            download: '',
        };
    }

    private resetVersionAndModel(): void {
        this.filters.update(f => ({ ...f, version: '', model: '' }));
        this.emitFiltersChange();
    }

    private emitFiltersChange(): void {
        this.filtersChanged.emit(this.createRequestPayload());
    }

    private getVehicleType(): string {
        const brands = this.brandOptions();
        if (brands.length > 0) {
            return brands[0].label.includes('MOTORRAD') ? '0' : '1';
        }
        return '1';
    }

    private isSingleSelection(event: SelectListOption | SelectListOption[] | null): event is SelectListOption {
        return !!event && !Array.isArray(event);
    }

    private setLoading(isLoading: boolean, text: string = 'Loading...'): void {
        const config: DsLoadingAreaConfiguration = {
            id: this.LOADING_ID,
            loadingTitle: 'Loading...',
            loadingText: text,
        };

        if (isLoading) {
            this.loadingAreaService.addLoadingArea(config);
        } else {
            this.loadingAreaService.removeLoadingArea(this.LOADING_ID);
        }
    }

    private addToast(tone: 'critical' | 'positive' | 'neutral', toastText: string): void {
        this.toastService.pushToast({
            id: 'replenishment-toast',
            tone,
            toastText,
        } as DsToastModel);
    }

    private getErrorMessage(error: unknown): string {
        const fallbackMessage = 'Error requesting replenishment vehicle';

        if (!error || typeof error !== 'object') {
            return fallbackMessage;
        }

        const err = error as {
            message?: string;
            error?: string | { message?: string; messageText?: string; error?: string };
        };

        if (typeof err.error === 'string' && err.error.trim()) {
            return err.error;
        }

        if (err.error && typeof err.error === 'object') {
            if (typeof err.error.messageText === 'string' && err.error.messageText.trim()) {
                return err.error.messageText;
            }
            if (typeof err.error.message === 'string' && err.error.message.trim()) {
                return err.error.message;
            }
            if (typeof err.error.error === 'string' && err.error.error.trim()) {
                return err.error.error;
            }
        }

        if (typeof err.message === 'string' && err.message.trim()) {
            return err.message;
        }

        return fallbackMessage;
    }
}
